"use client";

import { c as _c } from "react/compiler-runtime";
import { useState, useEffect, Suspense } from "react";
import Image from "next/image";
import { httpsCallable } from "firebase/functions";
import { doc, onSnapshot } from "firebase/firestore";
import { functions, auth, db } from "@/lib/firebase";
import { Loader2, Check } from "lucide-react";
import { useSearchParams, useRouter } from "next/navigation";
import holderPadel from "../../../public/drawables/holder_padel.webp";
import holderBadminton from "../../../public/drawables/holder_badminton.webp";
import holderSquash from "../../../public/drawables/holder_squash.webp";
const STRIPE_PRICE_ID_MVP_PADEL_1SET = process.env.NEXT_PUBLIC_STRIPE_PRICE_ID_MVP_PADEL_1SET || "";
const STRIPE_PRICE_ID_MVP_PADEL_2SET = process.env.NEXT_PUBLIC_STRIPE_PRICE_ID_MVP_PADEL_2SET || "";
const STRIPE_PRICE_ID_MVP_BADMINTON_1SET = process.env.NEXT_PUBLIC_STRIPE_PRICE_ID_MVP_BADMINTON_1SET || "";
const STRIPE_PRICE_ID_MVP_SQUASH_1SET = process.env.NEXT_PUBLIC_STRIPE_PRICE_ID_MVP_SQUASH_1SET || "";
const COUNTRIES = [{
  code: "AT",
  name: "Austria"
}, {
  code: "BE",
  name: "Belgium"
}, {
  code: "BG",
  name: "Bulgaria"
}, {
  code: "CH",
  name: "Switzerland"
}, {
  code: "CY",
  name: "Cyprus"
}, {
  code: "CZ",
  name: "Czech Republic"
}, {
  code: "DE",
  name: "Germany"
}, {
  code: "DK",
  name: "Denmark"
}, {
  code: "EE",
  name: "Estonia"
}, {
  code: "ES",
  name: "Spain"
}, {
  code: "FI",
  name: "Finland"
}, {
  code: "FR",
  name: "France"
}, {
  code: "GR",
  name: "Greece"
}, {
  code: "HR",
  name: "Croatia"
}, {
  code: "HU",
  name: "Hungary"
}, {
  code: "IE",
  name: "Ireland"
}, {
  code: "IT",
  name: "Italy"
}, {
  code: "LI",
  name: "Liechtenstein"
}, {
  code: "LT",
  name: "Lithuania"
}, {
  code: "LU",
  name: "Luxembourg"
}, {
  code: "LV",
  name: "Latvia"
}, {
  code: "MT",
  name: "Malta"
}, {
  code: "NL",
  name: "Netherlands"
}, {
  code: "NO",
  name: "Norway"
}, {
  code: "PL",
  name: "Poland"
}, {
  code: "PT",
  name: "Portugal"
}, {
  code: "RO",
  name: "Romania"
}, {
  code: "SE",
  name: "Sweden"
}, {
  code: "SI",
  name: "Slovenia"
}, {
  code: "SK",
  name: "Slovakia"
}];
function StoreContent() {
  const searchParams = useSearchParams();
  const [loadingPriceId, setLoadingPriceId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [ogSubscriptionsLeft, setOgSubscriptionsLeft] = useState<number>(0);
  const [shippingCountry, setShippingCountry] = useState<string>("");
  useEffect(() => {
    // Handle BFCache (Back/Forward Cache) restoration
    const handlePageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        setLoadingPriceId(null);
      }
    };
    window.addEventListener('pageshow', handlePageShow);
    return () => window.removeEventListener('pageshow', handlePageShow);
  }, []);
  useEffect(() => {
    if (searchParams.get("canceled")) {
      setLoadingPriceId(null);
      setError("Order canceled. You have not been charged.");
    }
  }, [searchParams]);
  useEffect(() => {
    const unsubscribe = onSnapshot(doc(db, "dbMVPlayer", "fldStats"), doc => {
      if (doc.exists()) {
        setOgSubscriptionsLeft(doc.data().originalGangsterSubscriptionsLeft || 0);
      }
    });
    return () => unsubscribe();
  }, []);
  const handleCheckout = async (priceId: string, mode: 'payment' | 'subscription' = 'payment') => {
    if (mode === 'payment' && !shippingCountry) {
      setError("Please select a shipping country first.");
      const selectElement = document.getElementById("shipping-country");
      if (selectElement) {
        selectElement.scrollIntoView({
          behavior: "smooth",
          block: "center"
        });
        selectElement.focus();
      }
      return;
    }
    setLoadingPriceId(priceId);
    setError(null);
    const user = auth.currentUser;
    if (!user) {
      setError("Please sign in to make a purchase.");
      setLoadingPriceId(null);
      return;
    }
    try {
      const createCheckoutSession = httpsCallable(functions, 'createCheckoutSession');

      // Call the Cloud Function
      const {
        data
      }: any = await createCheckoutSession({
        priceId: priceId,
        mode: mode,
        origin: window.location.origin,
        countryCode: mode === 'payment' ? shippingCountry : undefined
      });
      if (data?.url) {
        // Redirect directly to the Stripe Checkout URL returned by the backend
        window.location.assign(data.url);

        // Safety timeout: If the redirect opens in a new tab or fails to unload the page immediately,
        // reset the loading state after a few seconds so the user isn't stuck.
        setTimeout(() => {
          setLoadingPriceId(null);
        }, 3000);
      } else {
        throw new Error("No checkout URL returned from server.");
      }
    } catch (err: any) {
      console.error("Checkout failed:", err);
      setError(err.message || "Checkout failed. Please try again.");
      setLoadingPriceId(null);
    }
    // We don't unset loading here on success because the page will redirect
  };
  return <div className="animate-in fade-in duration-500">
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-24">
        <div className="text-center mb-16">
          <h1 className="text-4xl sm:text-5xl font-sriracha tracking-tighter text-[#B0D18B] mb-4 opacity-0 blur-md translate-y-8" style={{
          animation: "fadeSlideUnblur 1s ease-out 0.6s forwards"
        }}>
            MVP Smartphone Holders
          </h1>
          <p className="text-[#C4C8BA] max-w-2xl mx-auto mb-8 opacity-0 blur-md translate-y-8" style={{
          animation: "fadeSlideUnblur 1s ease-out 0.8s forwards"
        }}>
            Slide in your smartphone and start playing!
          </p>

          {/* Shipping Country Selector */}
          <div className="bg-[#1A1C16] border border-[#8E9285]/20 rounded-2xl p-6 max-w-sm mx-auto opacity-0 blur-md translate-y-8" style={{
          animation: "fadeSlideUnblur 1s ease-out 1.0s forwards"
        }}>
            <label htmlFor="shipping-country" className="block text-sm font-bold text-[#E2E3D8] mb-2 font-sriracha">
              Select Your Shipping Country
            </label>
            <select id="shipping-country" value={shippingCountry} onChange={e => setShippingCountry(e.target.value)} className={`w-full bg-[#12140E] border rounded-xl py-3 px-4 text-[#E2E3D8] focus:border-[#B0D18B] focus:outline-none transition-colors text-center ${error === "Please select a shipping country first." && !shippingCountry ? "border-red-500 animate-pulse shadow-[0_0_10px_rgba(239,68,68,0.2)]" : "border-[#8E9285]/30"}`}>
              <option value="">-- Choose Country --</option>
              {COUNTRIES.map(c => <option key={c.code} value={c.code}>
                  {c.name}
                </option>)}
            </select>
          </div>
        </div>
        
        <div className="max-w-4xl mx-auto space-y-16">
          {/* Golden Court Brackets Section */}
          <div className="space-y-8">
            {error && <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-4 text-center text-red-400 mb-8 max-w-md mx-auto">
                {error}
              </div>}
            
            <h2 className="text-2xl sm:text-3xl font-bold text-[#B0D18B] font-sriracha text-center mb-6 opacity-0 blur-md translate-y-8" style={{
            animation: "fadeSlideUnblur 1s ease-out 1.6s forwards"
          }}>
              Padel
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              {/* Set of 1 */}
              <div className="bg-[#1A1C16] border border-[#8E9285]/20 rounded-2xl p-6 flex flex-col opacity-0 blur-md translate-y-8 hover:border-[#B0D18B] transition-colors duration-300" style={{
              animation: "fadeSlideUnblur 1s ease-out 1.4s forwards"
            }}>
                  <div className="relative aspect-video mb-6 rounded-xl overflow-hidden bg-[#12140E] border border-[#8E9285]/10">
                      <Image src={holderPadel} alt="MVPlayer Padel Bracket" fill className="object-contain p-4" />
                  </div>
                  <h3 className="text-xl font-bold text-[#E2E3D8] font-sriracha mb-2">Padel Cage Holder</h3>
                  <p className="text-[#C4C8BA] text-sm mb-4">
                    Turn any smartphone into a side-court scoreboard.
                  </p>
                  
                  <ul className="space-y-3 mb-8 flex-1">
                      <li className="flex items-start gap-2 text-sm text-[#E2E3D8]">
                          <Check className="h-4 w-4 text-[#B0D18B] mt-0.5 shrink-0" />
                          <span>Fits any smartphone</span>
                      </li>
                      <li className="flex items-start gap-2 text-sm text-[#E2E3D8]">
                          <Check className="h-4 w-4 text-[#B0D18B] mt-0.5 shrink-0" />
                          <span>Sustainable 3D printed design</span>
                      </li>
                      <li className="flex items-start gap-2 text-sm text-[#E2E3D8]">
                          <Check className="h-4 w-4 text-[#B0D18B] mt-0.5 shrink-0" />
                          <span>Shipping to all European countries, Great-Britain and Ireland</span>
                      </li>
                  </ul>

                  <div className="mt-auto pt-4 border-t border-[#8E9285]/10 flex items-center justify-between">
                      <div className="text-2xl font-bold text-[#E2E3D8]">€9.99</div>
                      <button onClick={() => handleCheckout(STRIPE_PRICE_ID_MVP_PADEL_1SET, 'payment')} disabled={loadingPriceId !== null} className="bg-[#B0D18B] hover:bg-[#9DC077] text-[#1A1C16] font-bold py-3 px-6 rounded-full transition-all shadow-lg hover:shadow-[#B0D18B]/20 flex items-center justify-center gap-2 disabled:opacity-50">
                          {loadingPriceId === STRIPE_PRICE_ID_MVP_PADEL_1SET ? <Loader2 className="h-5 w-5 animate-spin" /> : "Order Now"}
                      </button>
                  </div>
              </div>

              {/* Set of 2 */}
              <div className="bg-[#1A1C16] border border-[#8E9285]/50 rounded-2xl p-6 flex flex-col relative overflow-hidden opacity-0 blur-md translate-y-8 hover:border-[#B0D18B] transition-colors duration-300 shadow-[0_0_20px_rgba(176,209,139,0.05)]" style={{
              animation: "fadeSlideUnblur 1s ease-out 1.5s forwards"
            }}>
                  <div className="absolute top-0 right-0 bg-[#B0D18B] text-[#1E3702] text-xs font-bold px-3 py-1 rounded-bl-xl z-20">
                      BEST VALUE
                  </div>
                  <div className="relative aspect-video mb-6 rounded-xl overflow-hidden bg-[#12140E] border border-[#8E9285]/10">
                      <Image src={holderPadel} alt="MVPlayer Padel Bracket MVP" fill className="object-contain p-4" />
                  </div>
                  <h3 className="text-xl font-bold text-[#E2E3D8] font-sriracha mb-2">Padel Cage Holder x2</h3>
                  <p className="text-[#C4C8BA] text-sm mb-4">
                    Turn any smartphone into a side-court scoreboard. Set of 2 pieces, one for you and your buddy.
                  </p>
                  
                  <ul className="space-y-3 mb-8 flex-1">
                      <li className="flex items-start gap-2 text-sm text-[#E2E3D8]">
                          <Check className="h-4 w-4 text-[#B0D18B] mt-0.5 shrink-0" />
                          <span>Fits any smartphone</span>
                      </li>
                      <li className="flex items-start gap-2 text-sm text-[#E2E3D8]">
                          <Check className="h-4 w-4 text-[#B0D18B] mt-0.5 shrink-0" />
                          <span>Sustainable 3D printed design</span>
                      </li>
                      <li className="flex items-start gap-2 text-sm text-[#E2E3D8]">
                          <Check className="h-4 w-4 text-[#B0D18B] mt-0.5 shrink-0" />
                          <span>Shipping to all European countries, Great-Britain and Ireland</span>
                      </li>
                  </ul>

                  <div className="mt-auto pt-4 border-t border-[#8E9285]/10 flex items-center justify-between">
                      <div className="text-2xl font-bold text-[#E2E3D8]">€18.00</div>
                      <button onClick={() => handleCheckout(STRIPE_PRICE_ID_MVP_PADEL_2SET, 'payment')} disabled={loadingPriceId !== null} className="bg-[#B0D18B] hover:bg-[#9DC077] text-[#1A1C16] font-bold py-3 px-6 rounded-full transition-all shadow-lg hover:shadow-[#B0D18B]/20 flex items-center justify-center gap-2 disabled:opacity-50">
                          {loadingPriceId === STRIPE_PRICE_ID_MVP_PADEL_2SET ? <Loader2 className="h-5 w-5 animate-spin" /> : "Order Now"}
                      </button>
                  </div>
              </div>
            </div>
          </div>

          {/* Badminton & Squash Holders Section */}
          <div className="space-y-8">
            <h2 className="text-2xl sm:text-3xl font-bold text-[#B0D18B] font-sriracha text-center mb-6 opacity-0 blur-md translate-y-8" style={{
            animation: "fadeSlideUnblur 1s ease-out 1.6s forwards"
          }}>
              Badminton & Squash
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              {/* Badminton */}
              <div className="bg-[#1A1C16] border border-[#8E9285]/20 rounded-2xl p-6 flex flex-col opacity-0 blur-md translate-y-8 hover:border-[#B0D18B] transition-colors duration-300" style={{
              animation: "fadeSlideUnblur 1s ease-out 1.7s forwards"
            }}>
                  <div className="relative aspect-video mb-6 rounded-xl overflow-hidden bg-[#12140E] border border-[#8E9285]/10">
                      <Image src={holderBadminton} alt="MVPlayer Badminton Bracket" fill className="object-contain p-4" />
                  </div>
                  <h3 className="text-xl font-bold text-[#E2E3D8] font-sriracha mb-2">Badminton Stand</h3>
                  <p className="text-[#C4C8BA] text-sm mb-4">
                    Turn any smartphone or tablet into a side-court scoreboard. For on the floor or on a table.
                  </p>
                  
                  <ul className="space-y-3 mb-8 flex-1">
                      <li className="flex items-start gap-2 text-sm text-[#E2E3D8]">
                          <Check className="h-4 w-4 text-[#B0D18B] mt-0.5 shrink-0" />
                          <span>Fits any smartphone</span>
                      </li>
                      <li className="flex items-start gap-2 text-sm text-[#E2E3D8]">
                          <Check className="h-4 w-4 text-[#B0D18B] mt-0.5 shrink-0" />
                          <span>Sustainable 3D printed design</span>
                      </li>
                      <li className="flex items-start gap-2 text-sm text-[#E2E3D8]">
                          <Check className="h-4 w-4 text-[#B0D18B] mt-0.5 shrink-0" />
                          <span>Shipping to all European countries, Great-Britain and Ireland</span>
                      </li>
                  </ul>

                  <div className="mt-auto pt-4 border-t border-[#8E9285]/10 flex items-center justify-between">
                      <div className="text-2xl font-bold text-[#E2E3D8]">€9.99</div>
                      <button onClick={() => handleCheckout(STRIPE_PRICE_ID_MVP_BADMINTON_1SET, 'payment')} disabled={loadingPriceId !== null} className="bg-[#B0D18B] hover:bg-[#9DC077] text-[#1A1C16] font-bold py-3 px-6 rounded-full transition-all shadow-lg hover:shadow-[#B0D18B]/20 flex items-center justify-center gap-2 disabled:opacity-50">
                          {loadingPriceId === STRIPE_PRICE_ID_MVP_BADMINTON_1SET ? <Loader2 className="h-5 w-5 animate-spin" /> : "Order Now"}
                      </button>
                  </div>
              </div>

              {/* Squash */}
              <div className="bg-[#1A1C16] border border-[#8E9285]/20 rounded-2xl p-6 flex flex-col opacity-0 blur-md translate-y-8 hover:border-[#B0D18B] transition-colors duration-300" style={{
              animation: "fadeSlideUnblur 1s ease-out 1.8s forwards"
            }}>
                  <div className="relative aspect-video mb-6 rounded-xl overflow-hidden bg-[#12140E] border border-[#8E9285]/10">
                      <Image src={holderSquash} alt="MVPlayer Squash Bracket" fill className="object-contain p-4" />
                  </div>
                  <h3 className="text-xl font-bold text-[#E2E3D8] font-sriracha mb-2">Squash Court Holder</h3>
                  <p className="text-[#C4C8BA] text-sm mb-4">
                    Mount your smartphone securely to the glass court walls.
                  </p>
                  
                  <ul className="space-y-3 mb-8 flex-1">
                      <li className="flex items-start gap-2 text-sm text-[#E2E3D8]">
                          <Check className="h-4 w-4 text-[#B0D18B] mt-0.5 shrink-0" />
                          <span>Fits any smartphone</span>
                      </li>
                      <li className="flex items-start gap-2 text-sm text-[#E2E3D8]">
                          <Check className="h-4 w-4 text-[#B0D18B] mt-0.5 shrink-0" />
                          <span>Sustainable 3D printed design</span>
                      </li>
                      <li className="flex items-start gap-2 text-sm text-[#E2E3D8]">
                          <Check className="h-4 w-4 text-[#B0D18B] mt-0.5 shrink-0" />
                          <span>Shipping to all European countries, Great-Britain and Ireland</span>
                      </li>
                  </ul>

                  <div className="mt-auto pt-4 border-t border-[#8E9285]/10 flex items-center justify-between">
                      <div className="text-2xl font-bold text-[#E2E3D8]">€9.99</div>
                      <button onClick={() => handleCheckout(STRIPE_PRICE_ID_MVP_SQUASH_1SET, 'payment')} disabled={loadingPriceId !== null} className="bg-[#B0D18B] hover:bg-[#9DC077] text-[#1A1C16] font-bold py-3 px-6 rounded-full transition-all shadow-lg hover:shadow-[#B0D18B]/20 flex items-center justify-center gap-2 disabled:opacity-50">
                          {loadingPriceId === STRIPE_PRICE_ID_MVP_SQUASH_1SET ? <Loader2 className="h-5 w-5 animate-spin" /> : "Order Now"}
                      </button>
                  </div>
              </div>
            </div>
          </div>

        </div>
      </section>
    </div>;
}
export default function StorePage() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <Suspense fallback={<div className="min-h-[50vh] flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-[#B0D18B]" /></div>}><StoreContent /></Suspense>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}